import { Loading, addToast } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { updateClassStatus } from '../../api/requests/classes';
import useNearestClasses from '../../hooks/useNearestClasses';
import useServerSettings from '../../hooks/useServerSettings';
import { useUserState } from '../../hooks/useUserState';
import { Class, ClassStatus } from '../../types/Class';
import showModalsectionClosedAt from '../../utils/showModalSectionClosedAt';
import { ClassCard, ConfirmStartDialog, EmptyStateMsg } from './parts';
import FutureClassesSearch from './parts/FutureClassesSearch';
import { ClassRecoveryModal } from '../classesHistory/parts';
import ConfirmRecoveryDialog from '../classesHistory/parts/ConfirmRecoveryDialog';
import {
  reScheduleClass,
  searchClasses,
  updateClassStatus as updateStatus,
} from '../../api/requests/classes';
import { SubmitData as RecoverySubmitData } from '../classesHistory/parts/ClassRecoveryModal';
import ChangeClassroomModal from './parts/ChangeClassroomModal';
import {
  compareTimes,
  rangesOverlap,
  isRangeRunning,
  makeRange,
} from '../../utils/timeModuleUtils';
import useTimeModules from '../../hooks/useTimeModules';

export default function StartClasses() {
  const history = useHistory();
  const { t } = useTranslation();
  const { userData } = useUserState();
  const [classToRecover, setClassToRecover] = useState<any>();
  const [
    recoveryFormData,
    setRecoveryFormData,
  ] = useState<RecoverySubmitData>();
  const futureSearchRef = useRef<any>(null);
  const [classToChangeRoom, setClassToChangeRoom] = useState<any>(null);
  const [showChangeRoomModal, setShowChangeRoomModal] = useState(false);

  const [showConfirmStartDialog, setShowConfirmStartDialog] = useState(false);
  const [classToStart, setClassToStart] = useState<Class | undefined>();
  const { nearestClasses, isLoading } = useNearestClasses({
    accountId: userData?.id,
  });
  const { offset } = useServerSettings();
  const { timeModules } = useTimeModules();
  const today = new Date();

  const startClass = useCallback(async () => {
    if (!userData?.id) {
      return;
    }
    if (classToStart) {
      const { id } = classToStart;

      try {
        await updateClassStatus({
          status: ClassStatus.INIT,
          id,
        });
        history.push(`/classes/${id}/attendance`);
      } catch (err) {
        console.error(err);
      }
    }
  }, [classToStart, history, userData]);

  const onClassAdvance = useCallback(
    (classData: any) => {
      if (classData.sectionClosedAt) {
        showModalsectionClosedAt(t);
      } else {
        setClassToStart(classData);
      }
    },
    [t],
  );

  useEffect(() => {
    async function handleStart() {
      if (!classToStart) return;

      try {
        const [classStartTime] = classToStart?.schedule.split(' - ');
        const [hours, minutes] = classStartTime
          .split(':')
          .map((n) => Number(n));

        const classDateTime = dayjs(classToStart.date)
          .utc()
          .utcOffset(offset)
          .hour(hours)
          .minute(minutes);
        const currentDateTime = dayjs().utc().utcOffset(offset).second(0);

        if (currentDateTime.isBefore(classDateTime)) {
          setShowConfirmStartDialog(true);
        } else {
          startClass();
        }
      } catch (err) {
        console.log(err);
      }
    }

    handleStart();
  }, [classToStart, startClass, offset]);

  const renderNearestClassList = () => {
    if (isLoading) {
      return (
        <div className="text-center loader">
          <Loading textBody={t('common.loading.body')} />
        </div>
      );
    }

    if (nearestClasses.length === 0) {
      return (
        <EmptyStateMsg
          msg={t(`startClasses.noScheduledClassesForToday`)}
          date={`Fecha: ${today
            .toLocaleDateString(undefined, {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })
            .replaceAll('/', '-')} Hora: ${today.toLocaleTimeString()}`}
        />
      );
    }

    return (
      <>
        <p>{t(`startClasses.pageDescription`)}</p>
        <div className="d-flex px-3 flex-wrap">
          {nearestClasses.map((c: Class) => (
            <ClassCard
              key={c.id}
              classData={c}
              onStartClick={() => setClassToStart(c)}
              onModifyClick={() => history.push(`/classes/${c.id}/attendance`)}
            />
          ))}
        </div>
      </>
    );
  };

  const isCurrentlyRunning = (
    startTime: string,
    endTime: string,
    date: string,
  ) => {
    return isRangeRunning(makeRange(startTime, endTime), date, offset);
  };

  const hasConflictWithAnotherClass = async (
    formData: RecoverySubmitData,
  ): Promise<boolean> => {
    const date = dayjs(formData.date).format('YYYY-MM-DD');

    const res = await searchClasses({
      sectionId: classToRecover.sectionId,
      page: 0,
      itemsPerPage: Number.MAX_SAFE_INTEGER,
      initialDate: date,
      endDate: date,
    });

    const classesList = res.data?.data?.filter((cd: any) => {
      return (
        cd.status !== ClassStatus.CLOSED && cd.status !== ClassStatus.CANCELLED
      );
    });

    if (!classesList || classesList.length === 0) {
      return false;
    }

    const newStart = formData.initModule.startTime;
    const newEnd = formData.endModule.endTime;

    if (compareTimes(newEnd, newStart) <= 0) {
      return false;
    }

    const conflict = classesList.some((cd: any) => {
      if (cd.id === classToRecover?.id) {
        return false;
      }
      const { startModule, endModule } = cd.sectionSchedule || {};
      if (!startModule || !endModule) {
        return false;
      }
      return rangesOverlap(
        { startTime: newStart, endTime: newEnd },
        { startTime: startModule.startTime, endTime: endModule.endTime },
      );
    });

    return conflict;
  };

  const recoverClass = async (
    formData: RecoverySubmitData,
    force?: boolean,
  ) => {
    const hasScheduleConflicts = await hasConflictWithAnotherClass(formData);

    if (!force && hasScheduleConflicts) {
      setRecoveryFormData(formData);
      return;
    }

    setRecoveryFormData(undefined);

    const res = await reScheduleClass({
      dateRecovery: dayjs(formData.date).format('YYYY-MM-DD'),
      startModuleId: formData.initModule.id,
      endModuleId: formData.endModule.id,
      lessonsId: classToRecover.id,
      sectionId: classToRecover.sectionId,
      classroomId: formData.classroomId,
    });

    const recoveredClassId = res.data?.data.id;

    if (res.error) {
      addToast({
        icon: 'information',
        color: 'danger',
        text: t(`classesHistory.errors.failedRecoveringClass`),
      });
    } else {
      addToast({
        icon: 'check',
        color: 'success',
        text: t(`classesHistory.successMsgs.classScheduled`),
      });

      const isRunning = isCurrentlyRunning(
        formData.initModule.startTime || '',
        formData.endModule.endTime || '',
        formData.date,
      );

      if (isRunning) {
        await updateStatus({
          status: ClassStatus.INIT,
          id: recoveredClassId as number,
        });
        history.push(`/classes/${recoveredClassId}/attendance`);
      } else {
        setClassToRecover(undefined);
        futureSearchRef.current?.repeatSearch?.();
      }
    }
  };

  const changeClassroom = async (classroomId: number) => {
    try {
      const [startStr, endStr] = (classToChangeRoom?.schedule || '')
        .split('-')
        .map((s: string) => s.trim());
      const startModule = timeModules.find(
        (tm) => tm.startTime.slice(0, 5) === startStr,
      );
      const endModule = timeModules.find(
        (tm) => tm.endTime.slice(0, 5) === endStr,
      );
      if (!startModule || !endModule) {
        addToast({
          icon: 'information',
          color: 'danger',
          text: t('startClasses.changeClassroomError'),
        });
        return;
      }
      await reScheduleClass({
        dateRecovery: classToChangeRoom.date,
        startModuleId: startModule.id,
        endModuleId: endModule.id,
        lessonsId: classToChangeRoom.id,
        sectionId: classToChangeRoom.sectionId,
        classroomId,
      });
      addToast({
        icon: 'check',
        color: 'success',
        text: t('startClasses.changeClassroomSuccess'),
      });
    } catch (err) {
      addToast({
        icon: 'information',
        color: 'danger',
        text: t('startClasses.changeClassroomError'),
      });
    } finally {
      setShowChangeRoomModal(false);
      setClassToChangeRoom(null);
      futureSearchRef.current?.repeatSearch?.();
    }
  };

  return (
    <>
      <ChangeClassroomModal
        isOpen={showChangeRoomModal}
        onClose={() => {
          setShowChangeRoomModal(false);
          setClassToChangeRoom(null);
        }}
        onConfirm={changeClassroom}
        lessonId={classToChangeRoom?.id || 0}
        date={classToChangeRoom?.date || dayjs().format('YYYY-MM-DD')}
        schedule={classToChangeRoom?.schedule || ''}
        courseName={classToChangeRoom?.courseName || ''}
        currentClassroomCode={classToChangeRoom?.currentClassroomCode}
        currentClassroomName={classToChangeRoom?.currentClassroomName}
        disabled={!classToChangeRoom}
      />
      <ConfirmRecoveryDialog
        isOpen={!!recoveryFormData}
        onConfirm={() =>
          recoverClass(recoveryFormData as RecoverySubmitData, true)
        }
        onCancel={() => setRecoveryFormData(undefined)}
      />
      <ClassRecoveryModal
        isOpen={!!classToRecover}
        onConfirm={recoverClass}
        onCancel={() => setClassToRecover(undefined)}
        courseName={classToRecover?.courseName || ''}
        lessonId={classToRecover?.id}
        currentClassroomCode={classToRecover?.classroom?.code}
        currentClassroomName={classToRecover?.classroom?.name}
      />
      <ConfirmStartDialog
        isOpen={showConfirmStartDialog}
        onCancel={() => setShowConfirmStartDialog(false)}
        onConfirm={() => startClass()}
        onDialogClose={() => setClassToStart(undefined)}
      />
      <div className="m-3 py-4 px-3 bg-white rounded">
        <div className="p-4 rounded todays-classes-section">
          <h2 className="text-primary fs-20 fw-700">
            {t(`startClasses.todaysClasses`)}
          </h2>
          {renderNearestClassList()}
        </div>

        <FutureClassesSearch
          ref={futureSearchRef}
          onClassAdvance={onClassAdvance}
          onClassRecovery={(classData: any) => setClassToRecover(classData)}
          onClassChangeClassroom={(classData: any) => {
            setClassToChangeRoom(classData);
            setShowChangeRoomModal(true);
          }}
        />
      </div>
    </>
  );
}
